import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Api } from '../../../services/api';
import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { EstimatorTemplate } from '../../../services/Template/estimator-template';
import { Toast } from 'primereact/toast';
import { useParams } from 'react-router-dom';
import { InputNumber } from 'primereact/inputnumber';
import { Accordion, AccordionTab } from 'primereact/accordion';
import './Estimating.css';
import '../../CRM/Leads/Leads.css';
import { FaCopy, FaPlus, FaTrash, FaSave } from 'react-icons/fa';
import WeeklyUpdatePreview from './WeeklyUpdatePreview';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from "primereact/inputtext";
import { Checkbox } from 'primereact/checkbox';
import { BsFillInfoCircleFill } from 'react-icons/bs';
import { classNames } from "primereact/utils";

// Module-level style objects — defined once, never recreated on re-render
const _headerStyle = { background: '#FEC025', border: '1px solid #E9ECEF', textAlign: 'center', fontSize: '14px' };
const _cellStyle = { border: '1px solid #E9ECEF', borderLeft: '2px solid #E9ECEF' };
const _cellStyleW = { border: '1px solid #E9ECEF', borderLeft: '2px solid #E9ECEF', backgroundColor: 'white', paddingTop: '5px' };
const _borderStyle = {};
const _yesCellStyle = { border: '1px solid #E9ECEF', borderLeft: '2px solid #E9ECEF' };
const _dropdownCellStyle = { border: '1px solid #E9ECEF', borderLeft: '2px solid #E9ECEF', padding: '0' };

// Module-level helpers — stable references, never cause re-renders
const _adjustTextareaHeight = (textarea) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.overflow = 'hidden';
    textarea.style.height = `${textarea.scrollHeight}px`;
};

const _syncTextareaHeightFromEvent = (event) => {
    const textarea = event?.target;
    requestAnimationFrame(() => _adjustTextareaHeight(textarea));
};

// Module-level textarea — local state prevents parent re-renders while typing
const LocalTextAreaField = React.memo(({ name, value, placeholder, rows = 2, disabled = false, onCommit, onDirty }) => {
    const [localValue, setLocalValue] = React.useState(value || '');
    const textareaRef = React.useRef(null);
    const onCommitRef = React.useRef(onCommit);
    const onDirtyRef = React.useRef(onDirty);

    React.useLayoutEffect(() => { onCommitRef.current = onCommit; });
    React.useLayoutEffect(() => { onDirtyRef.current = onDirty; });

    // Sync when external value changes (e.g. data load), not on local typing
    React.useEffect(() => { setLocalValue(value || ''); }, [value]);

    // Adjust height only on external value change
    React.useLayoutEffect(() => {
        if (textareaRef.current) _adjustTextareaHeight(textareaRef.current);
    }, [value]);

    const handleChange = React.useCallback((e) => {
        setLocalValue(e.target.value);
        _syncTextareaHeightFromEvent(e);
        onDirtyRef.current();
    }, []);

    const handleBlur = React.useCallback((e) => {
        onCommitRef.current(e.target.value);
    }, []);

    return (
        <textarea
            ref={textareaRef}
            name={name}
            id={name}
            rows={rows}
            placeholder={placeholder}
            className="w-full text-center p-2 mb-0"
            style={{ width: '100%', minHeight: '40px', boxSizing: 'border-box', border: 'none', outline: 'none', color: '#495057', paddingTop: '8px', paddingLeft: '5px', resize: 'none', overflowY: 'hidden', fontFamily: 'inherit', fontSize: 'inherit', backgroundColor: disabled ? '#f8f9fa' : 'white', cursor: disabled ? 'not-allowed' : 'text' }}
            value={localValue}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
        />
    );
// Custom comparator: only re-render when data props change.
// onCommit/onDirty are always current via refs, so skipping them is safe.
}, (prev, next) =>
    prev.name === next.name &&
    prev.value === next.value &&
    prev.placeholder === next.placeholder &&
    prev.rows === next.rows &&
    prev.disabled === next.disabled
);

// Cache role_id once at module level — avoids localStorage reads on every render
const _roleId = parseInt(localStorage.getItem('role_id'), 10);

const EditEstimating = ({ ProjectIdEdit }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [accordionActiveIndex, setAccordionActiveIndex] = useState([0, 1, 2, 3, 4, 5, 6]);
    var { editId } = useParams();
    const [financials, setFinancials] = useState([1, 2, 3, 4, 5, 6]);
    const [preConstructionShedule, setPreConstructionShedule] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]);

    const [projectBudget, setProjectBudget] = useState([1, 2, 3, 4, 5]);
    const [ActionItems, setActionItems] = useState([1]);
    const [Title, setTitle] = useState([1, 2, 3, 4, 5]);
    const [estimatorProjectName, setEstimatorProjectName] = useState('');
    const [contractAmount, setContractAmount] = useState('');
    const [isSaveBtnLoading, setIsSaveBtnLoading] = useState(false);
    const [internalJob, setInternalJob] = useState("N");
    const [showWeeklyPreview, setShowWeeklyPreview] = useState(false);
    const [previewValues, setPreviewValues] = useState(null);
    const containerRef = useRef(null);

    const elementRef0 = useRef(null);
    const elementRef1 = useRef(null);
    const elementRef2 = useRef(null);
    const elementRef3 = useRef(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const elementRefs = useMemo(() => [elementRef0, elementRef1, elementRef2, elementRef3], []);

    const expandAllTextareas = useCallback(() => {
        elementRefs.forEach(ref => {
            if (ref.current) {
                ref.current.querySelectorAll('textarea').forEach(_adjustTextareaHeight);
            }
        });

        if (containerRef.current) {
            containerRef.current.querySelectorAll('textarea').forEach(_adjustTextareaHeight);
        }
    }, [elementRefs]);

    const handleAddInputForFinancials = () => {
        var t = EstimatorTemplate.getFormName().section_2[0];
        t.Financials = '';
        setFinancials([...financials, t]);
        formik.values.section_2.push(t);
    };

    const [existPageWithoutSave, setExistPageWithoutSave] = useState(false);

    useEffect(() => {


        getTemplateDetails(ProjectIdEdit);

        return () => {
            //   formik.handleSubmit();
        };
    }, []);

    const getTemplateDetails = (id) => {

        var url = Api.API_BASE_URL + 'pre-con/get';
        formik.setFieldValue(`projectId`, id);

        const token = localStorage.getItem('authToken');

        // Create headers with token if it exists
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
        };
        fetch(url, {
            method: "POST",
            headers: headers,
            body: JSON.stringify({ projectId: id }),

        })
            .then((response) => response.json())
            .then((response) => {
                if (response.error === '') {
                    editId = response.Estimator;

                    // ── React state (non-formik) ──────────────────────────────
                    setEstimatorProjectName(response.EstimatorProjectName);
                    setInternalJob(response.sec_3.transferred_to_construction);

                    const sec_2 = response.sec_2.length > 0 ? response.sec_2 : formik.values.section_2;
                    if (response.sec_2.length > 0) setFinancials(response.sec_2);

                    const sec_4_raw = response.sec_4.length > 0 ? response.sec_4 : formik.values.section_4;
                    if (response.sec_4.length > 0) setPreConstructionShedule(response.sec_4);

                    const updatedSection5 = response.sec_5.length > 0
                        ? response.sec_5.map(item => ({ ...item, Amount: item.Amount === null ? '' : item.Amount }))
                        : formik.values.section_5;
                    if (response.sec_5.length > 0) setProjectBudget(updatedSection5);

                    const sec_6 = response.sec_6.length > 0 ? response.sec_6 : formik.values.section_6;
                    if (response.sec_6.length > 0) setActionItems(response.sec_6);

                    const sec_7 = response.sec_7.length > 0 ? response.sec_7 : formik.values.section_7;
                    if (response.sec_7.length > 0) setTitle(response.sec_7);

                    // Pre-populate section_4 dates when no saved data exists
                    let sec_4 = sec_4_raw;
                    if (response.sec_4.length === 0 && sec_4.length > 0) {
                        sec_4 = sec_4.map((row, i) => {
                            if (i === 0) return { ...row, PlannedCompletionDate: response.sec_3.scheduleConstructionStartDate, ActualCompletionDate: response.sec_3.scheduleConstructionStartDate, CurrentForecastCompletionDate: response.sec_3.scheduleProjectCompletion };
                            if (i === 12) return { ...row, PlannedCompletionDate: response.sec_3.scheduleProjectCompletion, CurrentForecastCompletionDate: response.sec_3.scheduleProjectCompletion };
                            return row;
                        });
                    }

                    // Compute costPerSFAmount
                    const totalSqFt = response.sec_3.totalSqFt ?? 0;
                    const computedCostPerSFAmount = (response.sec_3.totalSqFt && response.contractAmount && totalSqFt !== 0)
                        ? response.contractAmount / totalSqFt
                        : (response.sec_3.costPerSFAmount ?? 0);

                    // ── Single formik update — one render instead of ~25 ────
                    formik.setValues({
                        ...formik.values,
                        EstimatorId: response.Estimator,
                        projectId: response.sec_3.projectId,
                        contractAmount: response.contractAmount,
                        section_2: sec_2,
                        section_4: sec_4,
                        section_5: updatedSection5,
                        section_6: sec_6,
                        section_7: sec_7,
                        ...(response.sec_1 ? {
                            ConceptualEstimate: response.sec_1.ConceptualEstimate,
                            FSA: response.sec_1.FSA,
                            HardBid: response.sec_1.HardBid,
                            ChangeOrders: response.sec_1.ChangeOrders,
                            Selections: response.sec_1.Selections,
                            Total: response.sec_1.Total,
                            ProjectManager: response.sec_1.ProjectManager,
                            InteriorDesigner: response.sec_1.InteriorDesigner,
                            VPComments: response.sec_1.VPComments,
                        } : {}),
                        dear: response.sec_3.dear,
                        vpConstructionCommentsForAll: response.sec_3.vpConstructionCommentsForAll,
                        dearVPCComments: response.sec_3.dearVPCComments,
                        hereIsYourWeeklySummaryFrom: response.sec_3.hereIsYourWeeklySummaryFrom,
                        hereIsYourWeeklySummaryTo: response.sec_3.hereIsYourWeeklySummaryTo,
                        hereIsYourWeeklySummaryVPCComments: response.sec_3.hereIsYourWeeklySummaryVPCComments,
                        projectSummary: response.sec_3.projectSummary,
                        projectSummaryVPCComments: response.sec_3.projectSummaryVPCComments,
                        scheduleConstructionStartDate: response.sec_3.scheduleConstructionStartDate,
                        scheduleProjectCompletion: response.sec_3.scheduleProjectCompletion,
                        scheduleVPCComments: response.sec_3.scheduleVPCComments,
                        no_of_month: response.sec_3.no_of_month,
                        actual_start_month: response.sec_3.actual_start_month,
                        costPerSF: response.sec_3.costPerSF,
                        costPerSFComments: response.sec_3.costPerSFComments,
                        costPerSFAmount: computedCostPerSFAmount,
                        totalSqFt: totalSqFt,
                    });

                } else {
                    setInternalJob('N');
                    setEstimatorProjectName(response.EstimatorProjectName);
                    formik.setFieldValue('EstimatorId', 0);
                }
                setIsLoading(false);
                // Double-rAF: first frame lets React commit the setValues update,
                // second frame fires after the browser has laid out the new content.
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => { expandAllTextareas(); });
                });
            })
            .catch((error) => {
                console.error("Error:", error);
                setIsLoading(false);
            });
    };



    const handleDeleteInputForFinancials = (index) => {
        setFinancials([...financials, EstimatorTemplate.getFormName().section_2[0]]);
        var newArray = [...financials];
        newArray.splice(index, 1);
        formik.values.section_2.splice(index, 1);
        setFinancials(newArray);
    };

    const handleAddInputForPreConstructionShedule = () => {
        var t = EstimatorTemplate.getFormName().section_4[0];
        t.preConstructionShedule = '';
        t.Description = '';
        setPreConstructionShedule([...preConstructionShedule, t]);
        formik.values.section_4.push(t);
    };

    const handleDeleteInputForPreConstructionShedule = (index) => {
        setPreConstructionShedule([...preConstructionShedule, 2]);
        var newArray = [...preConstructionShedule];
        newArray.splice(index, 1);
        formik.values.section_4.splice(index, 1);
        setPreConstructionShedule(newArray);
    };

    const handleAddInputForProjectBudget = () => {
        var y = EstimatorTemplate.getFormName().section_5[0];
        y.ProjectBudget = 'tese';
        y.Description = '';
        y.Amount = '';
        const newProjectBudgetEntry = {
            ProjectBudget: "",
            Amount: "",
            PreConstManagerComments: "",
            VPComments: "",
        };
        if (formik) {
            formik.values.section_5 = [
                ...formik.values.section_5.slice(0, 1),
                newProjectBudgetEntry,
                ...formik.values.section_5.slice(1),
            ];
        }
        setProjectBudget([
            ...projectBudget.slice(0, 1),
            newProjectBudgetEntry,
            ...projectBudget.slice(1),
        ]);
    };

    const handleDeleteInputForProjectBudget = (index) => {
        setProjectBudget([...projectBudget, EstimatorTemplate.getFormName().section_5[0]]);
        var newArray = [...projectBudget];
        newArray.splice(index, 1);
        formik.values.section_5.splice(index, 1);
        setProjectBudget(newArray);
    };

    const handleAddInputForActionItems = () => {
        setActionItems([...ActionItems, EstimatorTemplate.getFormName().section_6[0]]);
        formik.values.section_6.push(EstimatorTemplate.getFormName().section_6[0]);
    };

    const handleDeleteInputForActionItems = (index) => {
        setActionItems([...ActionItems, EstimatorTemplate.getFormName().section_6[0]]);
        var newArray = [...ActionItems];
        newArray.splice(index, 1);
        formik.values.section_6.splice(index, 1);
        setActionItems(newArray);
    };

    const handleAddInputForTitle = () => {
        var z = EstimatorTemplate.getFormName().section_7[0];
        z.Title = '';
        z.Description = '';
        setTitle([...Title, z]);
        formik.values.section_7.push(z);
    };

    const handleDeleteInputForTitle = (index) => {
        setTitle([...Title, EstimatorTemplate.getFormName().section_7[0]]);
        var newArray = [...Title];
        newArray.splice(index, 1);
        formik.values.section_7.splice(index, 1);
        setTitle(newArray);
    };

    // Style objects live at module level (_headerStyle, _cellStyle, etc.) — aliased here for local use
    const headerStyle = _headerStyle;
    const cellStyle = _cellStyle;
    const cellStyleW = _cellStyleW;
    const borderStyle = _borderStyle;
    const yesCellStyle = _yesCellStyle;
    const dropdownCellStyle = _dropdownCellStyle;

    const priceBodyTemplatee = (rowData) => {
        if (rowData === '' || rowData === null) {
            return '';
        }
        let numberValue = Number(rowData);
        if (isNaN(numberValue)) {
            return '';
        }
        const isNegative = rowData < 0; // Check if the value is negative
        return <span style={{ color: isNegative ? "red" : "black" }}>
            {formatCurrency(rowData)}
        </span>
    };

    const formatCurrency = (value) => {
        return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    };

    //* ------------------
    const renderTextAreaAutoResize = (fieldName, placeholder = '', formData = '', rowCount = 2, disabled = false) => (
        <LocalTextAreaField
            name={fieldName}
            value={formData !== '' ? formData : formik.values[fieldName]}
            placeholder={placeholder}
            rows={rowCount}
            disabled={disabled}
            onCommit={(val) => formik.setFieldValue(fieldName, val)}
            onDirty={() => setExistPageWithoutSave(true)}
        />
    );

    const renderTextAreaFullAutoResize = (fieldName, placeholder = '', formData = '', rowCount = 2) => (
        <LocalTextAreaField
            name={fieldName}
            value={formData !== '' ? formData : formik.values[fieldName]}
            placeholder={placeholder}
            rows={rowCount}
            onCommit={(val) => formik.setFieldValue(fieldName, val)}
            onDirty={() => setExistPageWithoutSave(true)}
        />
    );

    const renderTextAreaFullDes = (fieldName, placeholder, formData = '', rowCount = 2) => {
        const isEditable = [1, 2, 12, 13].includes(_roleId);
        return (
            <LocalTextAreaField
                name={fieldName}
                value={formData !== '' ? formData : formik.values[fieldName]}
                placeholder={placeholder}
                rows={rowCount}
                disabled={!isEditable}
                onCommit={(val) => formik.setFieldValue(fieldName, val)}
                onDirty={() => setExistPageWithoutSave(true)}
            />
        );
    };

    const renderInputArea = (fieldName, placeholder = '', type = 'text', formData = '') => {
        let value = formData !== '' ? formData : formik.values[fieldName];
        if (type == 'date') {

            if (formData != null) {
                let timestamp = new Date(formData);



                if (!isNaN(timestamp.getTime())) {
                    value = timestamp.toISOString().split('T')[0];
                }
            }
        }

        // Define the background color based on the field name
        let backgroundColor = '';
        if (fieldName === 'section_4.0.PlannedCompletionDate') {
            backgroundColor = '#FFE1FF';
        } else if (fieldName === 'section_4.12.CurrentForecastCompletionDate') {
            backgroundColor = '#FFCFB3';
        } else if (fieldName === 'section_4.12.PlannedCompletionDate') {
            backgroundColor = '#FFEB55';
        } else if (fieldName === 'section_4.8.CurrentForecastCompletionDate') {
            backgroundColor = '#96CEB4';
        }


        return (
            <InputText
                type={type}
                placeholder={placeholder}
                name={fieldName}
                id={fieldName}
                className="w-full text-center p-2 mb-0"
                value={value}
                step="any"
                onChange={(e) => {
                    setExistPageWithoutSave(true);
                    formik.setFieldValue(fieldName, e.target.value);
                    if (fieldName === 'section_4.0.PlannedCompletionDate') {
                        formik.setFieldValue('scheduleConstructionStartDate', e.target.value);
                    }
                    if (fieldName === 'section_4.12.CurrentForecastCompletionDate') {
                        formik.setFieldValue('scheduleProjectCompletion', e.target.value);
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        // e.preventDefault();
                    }
                }}
                style={{ width: '100%', height: '40px', boxSizing: 'border-box', border: 'none', textAlign: 'center', marginTop: '0px', backgroundColor: backgroundColor }}
            />
        )
    };

    const renderInputAreaNum = (fieldName, formData = '') => (
        <InputNumber

            className="text-center"
            inputClassName={formik.values[fieldName] < 0 ? "is-negative" : "is-positive"}
            value={formData !== '' ? formData : formik.values[fieldName]}
            onChange={(e) => {
                setExistPageWithoutSave(true);
                formik.setFieldValue(fieldName, e.value);
            }}

            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                }
            }}
            style={{
                width: "100%",
                height: "40px",
                boxSizing: "border-box",
                border: "none",

                textAlign: "center !important",
                marginTop: "0px",
            }}
        />
    );

    const renderInputAreaForAmount = (fieldName, formData = '') => (
        <InputNumber
            inputId="currency-us"
            className="text-center"
            inputClassName={formik.values[fieldName] < 0 ? "is-negative" : "is-positive"}
            value={formData !== '' ? formData : formik.values[fieldName]}
            onChange={(e) => {
                setExistPageWithoutSave(true);
                formik.setFieldValue(fieldName, e.value);
            }}
            mode="currency"
            currency="USD"
            locale="en-US"
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                }
            }}
            style={{
                width: "100%",
                height: "40px",
                boxSizing: "border-box",
                border: "none",

                textAlign: "center !important",
                marginTop: "0px",
            }}
        />
    );

    const renderSelect = (fieldName, formdata = '') => {
        var val = (formdata !== '') ? formdata : formik.values[fieldName];
        if (val === undefined) {
            val = 'N';
        }
        return (
            <select
                name={fieldName}
                id={fieldName}
                value={formdata !== '' ? formdata : formik.values[fieldName]}
                className={'select-text-color-' + val}
                onChange={(e) => {
                    setExistPageWithoutSave(true);
                    formik.setFieldValue(fieldName, e.target.value);
                }}
                style={{ width: '100%', textAlign: 'center', color: 'white', backgroundColor: '#ff0000', border: 'none' }}
            >
                <option value="N">No</option>
                <option value="Y">Yes</option>
            </select>
        );
    };



    const renderSelectForCostPerSF = (fieldName) => (
        <select
            name={fieldName}
            id={fieldName}
            value={formik.values[fieldName]}
            onChange={(e) => {
                setExistPageWithoutSave(true);
                formik.setFieldValue(fieldName, e.target.value)
            }}
            style={{ width: '100%', textAlign: 'center', height: '45px', color: '#495057', border: '1px solid #DDDFE2', borderRight: 'none' }}>
            <option value="">Please choose an option</option>
            <option value="Custom Home with Basement">Custom Home with Basement</option>
            <option value="Custom Home Slab on Grade">Custom Home Slab on Grade</option>
            <option value="Custom Home Crawl">Custom Home Crawl</option>
            <option value="Town House">Town House</option>
            <option value="Condo Above Ground">Condo Above Ground</option>
            <option value="Condo 1 Level Underground">Condo 1 Level Underground</option>
            <option value="Addition">Addition</option>
            <option value="Whole home gut">Whole home gut</option>
            <option value="Tenent Improvement">Tenent Improvement</option>
            <option value="Commerical Build">Commerical Build</option>
            <option value="Renovation">Renovation</option>
        </select>
    );

    //! --------------

    const toast = useRef(null);

    const formik = useFormik({
        initialValues: EstimatorTemplate.getFormName(),
        validate: (data) => { },
        onSubmit: (data) => {
            setIsSaveBtnLoading(true);

            const token = localStorage.getItem('authToken');

            // Create headers with token if it exists
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
            };
            var url = Api.API_BASE_URL + 'pre-con/save';

            fetch(url, {
                method: "POST",
                headers: headers,
                body: JSON.stringify(data),
            })
                .then((response) => response.json())
                .then((Josn) => {
                    setIsSaveBtnLoading(false);
                    setExistPageWithoutSave(false);
                    if (Josn.error !== '') {
                        toast.current.show({ severity: 'error', summary: 'Error', detail: Josn.error });
                    } else {
                        toast.current.show({ severity: 'success', summary: 'Success', detail: Josn.result });
                    }
                })
                .catch((error) => {
                    setIsSaveBtnLoading(false);
                    console.error("Error:", error);
                });
        }
    });

    const BudgetRemaining = (index,) => {
        var value = formik.values.section_2[index].Budget - formik.values.section_2[index].AmountUsed;
        if (value === '' || value === null || value === undefined || value === 'NaN') {
            value = 0;
        }
        formik.values.section_2[index].BudgetRemaining = value;
        return (
            <span>{priceBodyTemplatee(formik.values.section_2[index].BudgetRemaining)}</span>
        );
    };

    const ProjectedBudgetToCompalte = (index,) => {
        var value = formik.values.section_2[index].EstimatedHoursToComplete * formik.values.section_2[index].ChargeOutRate;
        if (value === '' || value === null || value === undefined || value === 'NaN') {
            value = 0;
        }
        formik.values.section_2[index].ProjectedBudgetToComplete = value;
        return (
            <span>{priceBodyTemplatee(formik.values.section_2[index].ProjectedBudgetToComplete)}</span>
        );
    };

    const OverUnderBudget = (index,) => {
        var value = formik.values.section_2[index].BudgetRemaining - formik.values.section_2[index].ProjectedBudgetToComplete;
        if (value === '' || value === null || value === undefined || value === 'NaN') {
            value = 0;
        }
        formik.values.section_2[index].OverUnderBudget = value;
        return (
            <span>{priceBodyTemplatee(formik.values.section_2[index].OverUnderBudget)}</span>
        );
    };

    const calculateRemainingTotal = (index) => {
        var totalFsaBudget = 0;
        for (let i = 0; i < formik.values.section_5.length - 3; i++) {
            totalFsaBudget = totalFsaBudget + parseFloat(formik.values.section_5[i].Amount || 0);
        }
        if (index === formik.values.section_5.length - 3) {
            formik.values.section_5[formik.values.section_5.length - 3].Amount = totalFsaBudget;
            return totalFsaBudget;
        } else {
            var fsaRunningTotal = totalFsaBudget - (parseFloat(formik.values.section_5[formik.values.section_5.length - 2].Amount) || 0);
            fsaRunningTotal = isNaN(fsaRunningTotal) ? 0 : fsaRunningTotal;
            formik.values.section_5[formik.values.section_5.length - 1].Amount = fsaRunningTotal;
            return fsaRunningTotal;
        }
    };



    const deleteOrShowButtonShowInProjectBudget = (index, type) => {
        if (type === 'delete') {
            if (index === 0) {
                return false;
            }
        }
        if (formik.values.section_5[index].ProjectBudget === 'CWO total' || formik.values.section_5[index].ProjectBudget === 'Total FSA Budget' || formik.values.section_5[index].ProjectBudget === 'FSA Running Total' || formik.values.section_5[index].ProjectBudget === 'Total Cost Used') {
            return false;
        }
        return true;
    };

    const handleInternalJobChange = (isChecked) => {

        const token = localStorage.getItem('authToken');

        // Create headers with token if it exists
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
        };
        fetch(Api.API_BASE_URL + 'pre-con/transferred-to-construction', {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                status: isChecked ? 'Y' : 'N',
                id: formik.values.EstimatorId
            }),
        })
            .then(response => response.json())
            .then(data => { /* saved */ })
            .catch(error => console.error(error));
    };



    const CostPerSFAmount = () => {
        if (formik.values.totalSqFt === '' || formik.values.totalSqFt === null || formik.values.totalSqFt === undefined || formik.values.totalSqFt === 'NaN' || formik.values.totalSqFt === 0) {
            return '';

        }


        const value = formik.values.contractAmount / formik.values.totalSqFt;
        const costPerSFAmount = (value === null || value === undefined || value.toString() === 'NaN' || value === '' || value === Infinity) ? 0 : value;

        formik.values.costPerSFAmount = costPerSFAmount;
        return (
            <span>{priceBodyTemplatee(costPerSFAmount)}</span>
        );
    };

    return (
        <div className="est-template-container" ref={containerRef}>
            <Toast ref={toast} />
            <WeeklyUpdatePreview
                visible={showWeeklyPreview}
                onHide={() => setShowWeeklyPreview(false)}
                values={previewValues}
                projectName={estimatorProjectName}
            />
            {isLoading ? (
                <div className="est-loading">
                    Loading...
                    <div className="est-spinner"></div>
                </div>
            ) : (
                <form onSubmit={formik.handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div className="est-template-card">
                        {/* ── Header ── */}
                        <div className="est-template-header">
                            <div className="flex align-items-center gap-3">
                                <h2 className="est-template-title">Pre Construction Manager Playbook</h2>
                                <div className="est-pm-control">
                                    <label htmlFor="ingredient1">PM Control</label>
                                    <Checkbox
                                        inputId="ingredient1"
                                        name="internal_job"
                                        value={internalJob}
                                        checked={internalJob === 'Y'}
                                        onChange={(e) => {
                                            setInternalJob(e.target.checked ? 'Y' : 'N');
                                            handleInternalJobChange(e.target.checked);
                                        }}
                                    />
                                    <BsFillInfoCircleFill size={16} className='text-gray-400' title='This is for the PM to control. If its a check, the date can be directly entered in the construction area. Otherwise, all the dates in the construction area are controlled from the pre-construction page.' />
                                </div>
                            </div>
                            <div className="est-header-actions">
                                <Button className="est-btn-copy" icon={<FaCopy />} label="Preview Weekly Update" type="button" onClick={() => { setPreviewValues(formik.values); setShowWeeklyPreview(true); }} />
                                <Button type="submit" className="est-btn-save" icon={<FaSave />} label="SAVE" loading={isSaveBtnLoading} />
                            </div>
                        </div>

                        {/* ── Scrollable Body ── */}
                        <div className="est-template-body">

                            <Accordion multiple activeIndex={accordionActiveIndex} onTabChange={(e) => setAccordionActiveIndex(e.index)}>

                            {/* ══════ Section 1: Financials ══════ */}
                            <AccordionTab header="1. FINANCIALS">
                            <div className="est-section">

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '400px' }}>Conceptual Estimate</th>
                                                <th>FSA</th>
                                                <th>Hard Bid</th>
                                                <th>Change Orders</th>
                                                <th>Selections</th>
                                                <th>Total</th>
                                                <th>Project Manager</th>
                                                <th>Interior Designer</th>
                                                <th>VP Construction Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>{renderInputArea(`ConceptualEstimate`, '', 'text')}</td>
                                                <td>{renderInputArea(`FSA`, '', 'text')}</td>
                                                <td>{renderInputArea(`HardBid`, '', 'number')}</td>
                                                <td>{renderInputArea(`ChangeOrders`, '', 'number')}</td>
                                                <td>{renderInputArea(`Selections`, '', 'number')}</td>
                                                <td>{renderInputArea(`Total`, '', 'text')}</td>
                                                <td>{renderInputArea(`ProjectManager`, '', 'text')}</td>
                                                <td>{renderInputArea(`InteriorDesigner`, '', 'text')}</td>
                                                <td>{renderTextAreaFullAutoResize(`VPComments`, '')}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '420px' }}>Financials</th>
                                                <th style={{ width: '140px' }}>Budget</th>
                                                <th style={{ width: '140px' }}>Amount<br />Used</th>
                                                <th style={{ width: '160px' }}>Budget<br />Remaining</th>
                                                <th style={{ width: '160px' }}>Est. Hours<br />to Complete</th>
                                                <th style={{ width: '160px' }}>Charge Out<br />Rate</th>
                                                <th style={{ width: '180px' }}>Projected Budget<br />to Complete</th>
                                                <th style={{ width: '160px' }}>Over/Under<br />Budget</th>
                                                {/* <th>Comments</th> */}
                                                <th style={{ width: '80px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {financials.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{renderTextAreaAutoResize(`section_2.${index}.Financials`, '', formik.values.section_2[index].Financials)}</td>
                                                    <td>{renderInputAreaForAmount(`section_2.${index}.Budget`, formik.values.section_2[index].Budget)}</td>
                                                    <td>{renderInputAreaForAmount(`section_2.${index}.AmountUsed`, formik.values.section_2[index].AmountUsed)}</td>
                                                    <td className="est-static-cell">{BudgetRemaining(index)}</td>
                                                    <td>{renderInputArea(`section_2.${index}.EstimatedHoursToComplete`, formik.values.section_2[index].EstimatedHoursToComplete)}</td>
                                                    <td>{renderInputAreaForAmount(`section_2.${index}.ChargeOutRate`, formik.values.section_2[index].ChargeOutRate)}</td>
                                                    <td className="est-static-cell">{ProjectedBudgetToCompalte(index)}</td>
                                                    <td className="est-static-cell">{OverUnderBudget(index)}</td>
                                                    {/* <td>{renderTextAreaFull(`section_2.${index}.VPComments`, '', formik.values.section_2[index].VPComments)}</td> */}
                                                    <td className="est-action-cell">
                                                        {index > 0 ? (<FaTrash className='est-btn-delete' onClick={() => handleDeleteInputForFinancials(index)} />) : null}
                                                        {financials.length - 1 === index ? (<FaPlus className='est-btn-add' onClick={() => handleAddInputForFinancials()} />) : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <thead>
                                            <tr>
                                                <th>Cost Per SF</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={{ padding: '16px' }}>
                                                    <div className="est-cost-per-sf">
                                                        <div>
                                                            <label>Dropdown</label>
                                                            {renderSelectForCostPerSF('costPerSF')}
                                                        </div>
                                                        <div>
                                                            <label>
                                                                Total Sq Ft{' '}
                                                                <BsFillInfoCircleFill size={12} className='text-gray-400' title='This is the total livable SF of the project, including the garage and any unfinished space.' />
                                                            </label>
                                                            {renderInputAreaNum('totalSqFt')}
                                                        </div>
                                                        <div>
                                                            <label>Cost Per SF Amount</label>
                                                            <div style={{ textAlign: 'center', padding: '8px 0', fontWeight: 500 }}>
                                                                {CostPerSFAmount()}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label>Comments</label>
                                                            {renderTextAreaFullAutoResize('costPerSFComments', 'comments')}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            </AccordionTab>

                            {/* ══════ Section 2: Project Address ══════ */}
                            <AccordionTab header="2. PROJECT ADDRESS">
                            <div className="est-section" ref={elementRefs[0]}>

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <tbody>
                                            <tr>
                                                <td className="est-label-cell" style={{ width: '100px' }}>Dear</td>
                                                <td>{renderTextAreaAutoResize('dear', '', formik.values.dear)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className="est-info-subtitle">
                                    Weekly Summary
                                    <sup> <BsFillInfoCircleFill size={12} className='text-gray-400' title='The date is updated automatically every week when it is archived.' /></sup>
                                </div>

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <thead>
                                            <tr>
                                                <th>From</th>
                                                <th>To</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    <input
                                                        type="date"
                                                        name='hereIsYourWeeklySummaryFrom'
                                                        value={formik.values.hereIsYourWeeklySummaryFrom == null ? formik.values.hereIsYourWeeklySummaryFrom : !isNaN(new Date(formik.values.hereIsYourWeeklySummaryFrom).getTime()) ? new Date(formik.values.hereIsYourWeeklySummaryFrom).toISOString().split('T')[0] : formik.values.hereIsYourWeeklySummaryFrom}
                                                        onChange={(e) => {
                                                            setExistPageWithoutSave(true);
                                                            formik.setFieldValue('hereIsYourWeeklySummaryFrom', e.target.value);
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="date"
                                                        name='hereIsYourWeeklySummaryTo'
                                                        value={formik.values.hereIsYourWeeklySummaryTo == null ? formik.values.hereIsYourWeeklySummaryTo : !isNaN(new Date(formik.values.hereIsYourWeeklySummaryTo).getTime()) ? new Date(formik.values.hereIsYourWeeklySummaryTo).toISOString().split('T')[0] : formik.values.hereIsYourWeeklySummaryTo}
                                                        onChange={(e) => {
                                                            setExistPageWithoutSave(true);
                                                            formik.setFieldValue('hereIsYourWeeklySummaryTo', e.target.value);
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className="est-info-subtitle">Project Summary</div>

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <tbody>
                                            <tr>
                                                <td>{renderTextAreaFullAutoResize('projectSummary', '')}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className="est-info-subtitle">Schedules</div>

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <thead>
                                            <tr>
                                                <th>FSA Start Date <sup><BsFillInfoCircleFill size={10} title='This date is Coming From: FSA signed - Planned Completion Date' /></sup></th>
                                                <th>Building Permit Received Date <sup><BsFillInfoCircleFill size={10} title='This date is Coming From: Building Permit Received - Forecasted Completion Date' /></sup></th>
                                                <th>Delayed Construction <sup><BsFillInfoCircleFill size={10} title='Is the number of months the project will be delayed after the building permit is received if the project cannot start when the building permit is received. Leave 0 if planning to start shortly after the building permit is received.' /></sup></th>
                                                <th>Construction Months <sup><BsFillInfoCircleFill size={10} title='Is the estimated number of months the project will take to complete from start to finish.' /></sup></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="est-date-pink">
                                                    <input
                                                        type="date"
                                                        name='scheduleConstructionStartDate'
                                                        value={formik.values.scheduleConstructionStartDate == null ? formik.values.scheduleConstructionStartDate : !isNaN(new Date(formik.values.scheduleConstructionStartDate).getTime()) ? new Date(formik.values.scheduleConstructionStartDate).toISOString().split('T')[0] : formik.values.scheduleConstructionStartDate}
                                                        readOnly
                                                        onChange={(e) => {
                                                            setExistPageWithoutSave(true);
                                                            formik.setFieldValue('scheduleConstructionStartDate', e.target.value);
                                                        }}
                                                    />
                                                </td>
                                                <td className="est-date-orange">
                                                    <input
                                                        type="date"
                                                        name='scheduleProjectCompletion'
                                                        value={formik.values.scheduleProjectCompletion == null ? formik.values.scheduleProjectCompletion : !isNaN(new Date(formik.values.scheduleProjectCompletion).getTime()) ? new Date(formik.values.scheduleProjectCompletion).toISOString().split('T')[0] : formik.values.scheduleProjectCompletion}
                                                        readOnly
                                                        onChange={(e) => {
                                                            setExistPageWithoutSave(true);
                                                            formik.setFieldValue('scheduleProjectCompletion', e.target.value);
                                                        }}
                                                    />
                                                </td>
                                                <td>{renderInputArea('actual_start_month', '', 'number')}</td>
                                                <td>{renderInputArea('no_of_month', '', 'number')}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            </AccordionTab>

                            {/* ══════ Section 3: Pre Construction Scheduled Milestones ══════ */}
                            <AccordionTab header="3. PRE CONSTRUCTION SCHEDULED MILESTONES">
                            <div className="est-section" ref={elementRefs[1]}>

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '400px' }}>Pre Construction Scheduled Milestones</th>
                                                <th>Planned Completion Date</th>
                                                <th>Forecasted Completion Date</th>
                                                <th>Actual Completion Date</th>
                                                <th>Description</th>
                                                <th>Notes</th>
                                                <th style={{ width: '80px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preConstructionShedule.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{renderTextAreaAutoResize(`section_4.${index}.preConstructionShedule`, '', formik.values.section_4[index].preConstructionShedule)}</td>
                                                    <td>{renderInputArea(`section_4.${index}.PlannedCompletionDate`, '', 'date', formik.values.section_4[index].PlannedCompletionDate)}</td>
                                                    <td>{renderInputArea(`section_4.${index}.CurrentForecastCompletionDate`, '', 'date', formik.values.section_4[index].CurrentForecastCompletionDate)}</td>
                                                    <td>{renderInputArea(`section_4.${index}.ActualCompletionDate`, '', 'date', formik.values.section_4[index].ActualCompletionDate)}</td>
                                                    <td>{renderTextAreaFullDes(`section_4.${index}.Description`, '', formik.values.section_4[index].Description)}</td>
                                                    <td>{renderTextAreaFullAutoResize(`section_4.${index}.Notes`, '', formik.values.section_4[index].Notes)}</td>
                                                    <td className="est-action-cell">
                                                        {index > 0 ? (<FaTrash className='est-btn-delete' onClick={() => handleDeleteInputForPreConstructionShedule(index)} />) : null}
                                                        {preConstructionShedule.length - 1 === index ? (<FaPlus className='est-btn-add' onClick={() => handleAddInputForPreConstructionShedule()} />) : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            </AccordionTab>

                            {/* ══════ Section 4: Project Budget ══════ */}
                            <AccordionTab header="4. PROJECT BUDGET">
                            <div className="est-section" ref={elementRefs[2]}>

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '400px' }}>Project Budget</th>
                                                <th>Amount</th>
                                                <th>Pre Construction Manager Comments</th>
                                                <th style={{ width: '80px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {projectBudget.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        {renderTextAreaAutoResize(`section_5.${index}.ProjectBudget`, '', formik.values.section_5[index].ProjectBudget, 2, !deleteOrShowButtonShowInProjectBudget(index, 'text'))}
                                                    </td>
                                                    <td>
                                                        {index === formik.values.section_5.length - 1 || index === formik.values.section_5.length - 3 ? (
                                                            <InputNumber
                                                                inputId="currency-us"
                                                                className="text-center"
                                                                inputClassName={calculateRemainingTotal(index) < 0 ? "is-negative" : "is-positive"}
                                                                placeholder=""
                                                                value={calculateRemainingTotal(index)}
                                                                mode="currency"
                                                                currency="USD"
                                                                locale="en-US"
                                                                style={{ width: "100%", height: "40px", boxSizing: "border-box", border: "none", textAlign: "center", marginTop: "0px" }}
                                                            />
                                                        ) : (
                                                            renderInputAreaForAmount(`section_5.${index}.Amount`, formik.values.section_5[index].Amount)
                                                        )}
                                                    </td>
                                                    <td>{renderTextAreaFullAutoResize(`section_5.${index}.PreConstManagerComments`, '', formik.values.section_5[index].PreConstManagerComments)}</td>
                                                    <td className="est-action-cell">
                                                        {deleteOrShowButtonShowInProjectBudget(index, 'delete') ? (<FaTrash className='est-btn-delete' onClick={() => handleDeleteInputForProjectBudget(index)} />) : null}
                                                        {index === 0 ? (<FaPlus className='est-btn-add' onClick={() => handleAddInputForProjectBudget()} />) : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            </AccordionTab>

                            {/* ══════ Section 5: Action Items ══════ */}
                            <AccordionTab header="5. ACTION ITEMS">
                            <div className="est-section" ref={elementRefs[3]}>

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <thead>
                                            <tr>
                                                <th>Action Items</th>
                                                <th style={{ width: '80px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ActionItems.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{renderTextAreaFullAutoResize(`section_6.${index}.ActionItems`, '', formik.values.section_6[index].ActionItems)}</td>
                                                    <td className="est-action-cell">
                                                        {index > 0 ? (<FaTrash className='est-btn-delete' onClick={() => handleDeleteInputForActionItems(index)} />) : null}
                                                        {ActionItems.length - 1 === index ? (<FaPlus className='est-btn-add' onClick={() => handleAddInputForActionItems()} />) : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            </AccordionTab>

                            {/* ══════ Section 6: Planned Baseline ══════ */}
                            <AccordionTab header="6. PLANNED BASELINE">
                            <div className="est-section">

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '400px' }}>Planned Baseline</th>
                                                <th><span>Planned<br />Completion Date</span></th>
                                                <th><span>Current Forecast<br />Completion Date</span></th>
                                                <th><span>Actual Completion<br />Date</span></th>
                                                <th style={{ width: '120px', minWidth: '120px' }}>Done</th>
                                                <th><span>Pre Construction Manager <br />Comments</span></th>
                                                {/* <th>Pre Construction Manager Comments</th> */}
                                                <th>VP Ops Comments</th>
                                                <th style={{ width: '80px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Title.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{renderTextAreaFullAutoResize(`section_7.${index}.Title`, '', formik.values.section_7[index].Title)}</td>
                                                    <td>{renderInputArea(`section_7.${index}.PlannedCompletionDate`, '', 'date', formik.values.section_7[index].PlannedCompletionDate)}</td>
                                                    <td>{renderInputArea(`section_7.${index}.CurrentForecastCompletionDate`, '', 'date', formik.values.section_7[index].CurrentForecastCompletionDate)}</td>
                                                    <td>{renderInputArea(`section_7.${index}.ActualCompletionDate`, '', 'date', formik.values.section_7[index].ActualCompletionDate)}</td>
                                                    <td>{renderSelect(`section_7.${index}.Done`, formik.values.section_7[index].Done)}</td>
                                                    <td>{renderTextAreaFullAutoResize(`section_7.${index}.PreConstManagerComments`, '', formik.values.section_7[index].PreConstManagerComments)}</td>
                                                    <td>{renderTextAreaFullAutoResize(`section_7.${index}.VPComments`, '', formik.values.section_7[index].VPComments)}</td>
                                                    <td className="est-action-cell">
                                                        {index > 0 ? (<FaTrash className='est-btn-delete' onClick={() => handleDeleteInputForTitle(index)} />) : null}
                                                        {Title.length - 1 === index ? (<FaPlus className='est-btn-add' onClick={() => handleAddInputForTitle()} />) : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            </AccordionTab>

                            {/* ══════ Section 7: VP Construction Ops Comments ══════ */}
                            <AccordionTab header="7. VP CONSTRUCTION OPS COMMENTS">
                            <div className="est-section">

                                <div className="est-table-wrap">
                                    <table className="est-table">
                                        <thead>
                                            <tr>
                                                <th>VP Construction Ops Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>{renderTextAreaFullAutoResize('vpConstructionCommentsForAll', '', '', 6)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            </AccordionTab>

                            </Accordion>

                        </div>
                    </div>
                </form>
            )}
        </div>
    );
}

export default EditEstimating;
